import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Mail, ArrowRight, Check, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { facebookConnect, linkedInConnect, sendVerificationCode, verifyCode } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { postLoginPath } from '../utils/returnUrl';
import './LoginModal.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_REDIRECT_URI = `${window.location.origin}/auth/google/callback`;
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '';
const LINKEDIN_CLIENT_ID = process.env.REACT_APP_LINKEDIN_CLIENT_ID || '';
const LINKEDIN_REDIRECT_URI = process.env.REACT_APP_LINKEDIN_REDIRECT_URI || `${window.location.origin}/auth/linkedin/callback`;

const LoginModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('initial'); // 'initial' | 'codeSent'
  const [showTerms, setShowTerms] = useState(false); // 'terms' | 'privacy' | 'consent' | false
  // Dijital Temsil ve Otomasyon Açık Rıza Onayı — hizmetin SaaS lisansı
  // olduğu beyanı da aynı onayın "Hizmetin Niteliği" bölümünde yer alıyor,
  // tek checkbox ile onaylanıyor
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const codeInputsRef = useRef([]);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const top = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (top) window.scrollTo(0, parseInt(top || '0') * -1);
    }
    return () => {
      const top = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (top) window.scrollTo(0, parseInt(top || '0') * -1);
    };
  }, [isOpen]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setStep('initial');
      setEmail('');
      setCode(['', '', '', '', '', '']);
      setError('');
      setLoading(false);
      setCountdown(0);
      setShowTerms(false);
      setConsentAccepted(false);
    }
  }, [isOpen]);

  // Social login result handler
  const handleSocialResult = useCallback((result) => {
    if (result.status && result.data?.token) {
      login(result.data);
      onClose();
      navigate(postLoginPath(result.data.candidateId));
    } else {
      setError(result.message || 'Giriş başarısız.');
    }
  }, [login, onClose, navigate]);

  // Google — redirect to authorization code flow
  const handleGoogleLogin = useCallback(() => {
    if (!GOOGLE_CLIENT_ID) {
      setError('Google login şu an kullanılamıyor.');
      return;
    }
    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(GOOGLE_REDIRECT_URI)}&response_type=code&scope=openid%20email%20profile`;
    window.location.href = url;
  }, []);

  // Facebook
  const handleFacebookLogin = () => {
    setError('');
    if (!window.FB) {
      setError('Facebook SDK yüklenemedi.');
      return;
    }
    window.FB.login((response) => {
      if (response.authResponse) {
        setLoading(true);
        facebookConnect(response.authResponse.accessToken)
          .then(handleSocialResult)
          .catch(() => setError('Facebook ile giriş başarısız.'))
          .finally(() => setLoading(false));
      }
    }, { scope: 'email,public_profile' });
  };

  // LinkedIn (redirect-based)
  const handleLinkedInLogin = () => {
    setError('');
    const state = Math.random().toString(36).substring(2);
    sessionStorage.setItem('linkedin_state', state);
    const url = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${LINKEDIN_CLIENT_ID}&redirect_uri=${encodeURIComponent(LINKEDIN_REDIRECT_URI)}&scope=openid%20profile%20email&state=${state}`;
    window.location.href = url;
  };

  // Email submit — doğrulama kodu gönder
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !consentAccepted) return;
    setLoading(true);
    setError('');

    try {
      const result = await sendVerificationCode(email);
      if (result.status) {
        setStep('codeSent');
        setCountdown(300); // 5 dakika
      } else {
        setError(result.message || 'Kod gönderilemedi.');
      }
    } catch {
      setError('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  // Code input handlers
  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      codeInputsRef.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits filled
    if (newCode.every(d => d !== '') && newCode.join('').length === 6) {
      handleVerifyCode(newCode.join(''));
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputsRef.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      e.preventDefault();
      const newCode = pasted.split('');
      setCode(newCode);
      codeInputsRef.current[5]?.focus();
      handleVerifyCode(pasted);
    }
  };

  const handleVerifyCode = async (codeStr) => {
    setLoading(true);
    setError('');
    try {
      const result = await verifyCode(email, codeStr);
      if (result.status && result.data?.token) {
        login(result.data);
        onClose();
        navigate(postLoginPath(result.data.candidateId));
      } else {
        setError(result.message || 'Kod geçersiz veya süresi dolmuş.');
      }
    } catch (e) {
      setError(e.message || 'Kod doğrulanamadı.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await sendVerificationCode(email);
      if (result.status) {
        setCode(['', '', '', '', '', '']);
        setCountdown(300);
      } else {
        setError(result.message || 'Kod tekrar gönderilemedi.');
      }
    } catch {
      setError('Bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const formatCountdown = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;


  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return ReactDOM.createPortal(
    <div className="login-overlay" onClick={handleOverlayClick}>
      <div className="login-modal">
        <button className="login-modal__close" onClick={onClose}>
          <X size={20} />
        </button>

        {step === 'initial' && (
          <>
            <div className="login-modal__header">
              <h2 className="login-modal__title">Giriş Yap</h2>
              <p className="login-modal__desc">Hesabınla giriş yap veya yeni hesap oluştur</p>
            </div>

            {error && <p className="login-modal__error">{error}</p>}

            {/* Zorunlu onay — kayıt/giriş öncesi */}
            <div className="login-modal__consent">
              <label className="login-modal__checkbox">
                <input
                  type="checkbox"
                  checked={consentAccepted}
                  onChange={(e) => setConsentAccepted(e.target.checked)}
                />
                <span>
                  <button type="button" className="login-modal__terms-link" onClick={() => setShowTerms('consent')}>Dijital Temsil ve Otomasyon Açık Rıza Onayı</button>'nı
                  okudum; bilgilerimin kamu/özel ilan platformlarında otomatik işlenmesi ve form
                  doldurma süreçlerinde yazılıma dijital temsil yetkisi verdiğimi, bu hizmetin işe
                  aracılık değil kişisel veri işleme ve otomasyon yazılım lisansı olduğunu kabul ediyorum.
                </span>
              </label>
            </div>

            {/* Social Login */}
            <div className="login-modal__socials">
              <button className="login-social login-social--google" onClick={handleGoogleLogin} disabled={loading || !consentAccepted}>
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span>Google ile devam et</span>
              </button>

              <button className="login-social login-social--facebook" onClick={handleFacebookLogin} disabled={loading || !consentAccepted}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span>Facebook ile devam et</span>
              </button>

              <button className="login-social login-social--linkedin" onClick={handleLinkedInLogin} disabled={loading || !consentAccepted}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#0A66C2">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                <span>LinkedIn ile devam et</span>
              </button>
            </div>

            <div className="login-modal__divider">
              <span>veya</span>
            </div>

            {/* Email Login */}
            <form className="login-modal__email-form" onSubmit={handleEmailSubmit}>
              <p className="login-modal__email-label">E-posta ile giriş yap</p>
              <div className="login-modal__input-group">
                <Mail size={18} className="login-modal__input-icon" />
                <input
                  type="email"
                  className="login-modal__input"
                  placeholder="ornek@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="login-modal__submit" disabled={loading || !consentAccepted}>
                {loading ? <><Loader2 size={16} className="login-modal__spinner" /> Gönderiliyor...</> : <>Devam Et <ArrowRight size={16} /></>}
              </button>
            </form>

            <p className="login-modal__terms-text">
              <button type="button" className="login-modal__terms-link" onClick={() => setShowTerms('terms')}>Kullanım Koşulları</button>'nı ve <button type="button" className="login-modal__terms-link" onClick={() => setShowTerms('privacy')}>Gizlilik Politikası</button>'nı inceleyebilirsiniz.
            </p>
          </>
        )}

        {showTerms && (
          <div className="login-modal__terms-overlay" onClick={() => setShowTerms(false)}>
            <div className="login-modal__terms-content" onClick={(e) => e.stopPropagation()}>
              <button className="login-modal__terms-close" onClick={() => setShowTerms(false)}>
                <X size={18} />
              </button>

              {showTerms === 'terms' && (
                <>
                  <h3>Kullanım Koşulları</h3>
                  <div className="login-modal__terms-body">
                    <p><strong>1. Genel Hükümler</strong></p>
                    <p>Bu platform, HireThink Yapay Zeka Teknolojileri A.Ş. ("HireThink") tarafından işletilmektedir. Platforma erişerek veya kullanarak bu koşulları kabul etmiş sayılırsınız.</p>

                    <p><strong>2. Hizmet Tanımı</strong></p>
                    <p>isBulsun.com, yapay zeka destekli bir kariyer ve ilan otomasyon asistanı yazılımıdır (SaaS). Kullanıcılar CV oluşturabilir, ilanlarla profil eşleştirmesi yaptırabilir ve onay verdikleri ilanlar için form taslağı hazırlama sürecini yazılıma bırakabilir; taslak yalnızca kullanıcının onayıyla gönderilir. Bu hizmet bir işe yerleştirme garantisi, iş bulma veya işe aracılık hizmeti değildir.</p>

                    <p><strong>3. Kullanıcı Yükümlülükleri</strong></p>
                    <p>Kullanıcılar doğru ve güncel bilgi sağlamakla yükümlüdür. Platformun kötüye kullanılması, sahte bilgi girilmesi veya üçüncü kişilerin haklarının ihlal edilmesi yasaktır.</p>

                    <p><strong>4. Fikri Mülkiyet</strong></p>
                    <p>Platformdaki tüm içerik, tasarım, logo ve yazılım HireThink'e aittir. İzinsiz kopyalanması, çoğaltılması veya dağıtılması yasaktır.</p>

                    <p><strong>5. Sorumluluk Sınırı</strong></p>
                    <p>HireThink, platformda yayınlanan iş ilanlarının doğruluğundan sorumlu değildir. İşveren tarafından sağlanan bilgilerin kontrolü işverene aittir.</p>

                    <p><strong>6. Değişiklikler</strong></p>
                    <p>HireThink bu koşulları önceden bildirimde bulunmaksızın değiştirme hakkını saklı tutar. Güncel koşullar platformda yayınlanır.</p>

                    <p><strong>7. İletişim</strong></p>
                    <p>HireThink Yapay Zeka Teknolojileri A.Ş.<br />Bilişim Vadisi, Ayazağa, Vadi Istanbul Park Etabı, Kemerburgaz Caddesi D:7A Blok, 34475 Sarıyer / İstanbul<br />info@hirethink.ai | 0850 307 80 61</p>
                  </div>
                </>
              )}

              {showTerms === 'consent' && (
                <>
                  <h3>Dijital Temsil ve Otomasyon Açık Rıza Onayı</h3>
                  <div className="login-modal__terms-body">
                    <p><strong>Açık Rıza</strong></p>
                    <p>Kullanıcı, platform üzerinde tanımladığı bilgiler doğrultusunda, kendi
                    adına kamu veya özel sektör iş ilan sistemlerinde form doldurma ve başvuru
                    adımlarını yürütmek üzere HireThink Yapay Zeka Teknolojileri A.Ş. yazılımına
                    dijital temsil yetkisi verdiğini kabul ve beyan eder.</p>

                    <p><strong>Kapsam</strong></p>
                    <p>Bu onay yalnızca kullanıcının profiline yüksek uyum gösteren ve
                    kullanıcının belirlediği tercihlere uyan ilanlar için, kullanıcının
                    sağladığı CV ve profil bilgileri kullanılarak kullanılır.</p>

                    <p><strong>Hizmetin Niteliği</strong></p>
                    <p>Sunulan hizmet bir işe yerleştirme garantisi, iş bulma veya işe aracılık
                    hizmeti değil, Veri Analiz ve Form Taslak Hazırlama Yazılımı lisansıdır
                    (SaaS). Yazılım, eşleşen ilanlar için form taslağı hazırlar; taslak yalnızca
                    kullanıcının onayıyla gönderilir. isBulsun.com, işverenlerin karar veya işe
                    alım süreçleri üzerinde bir garanti veya taahhütte bulunmaz.</p>

                    <p><strong>Geri Alma</strong></p>
                    <p>Bu onay, panel üzerinden ilgili özellik kapatılarak dilediğiniz zaman
                    geri alınabilir; geri alma, daha önce gönderilmiş formları etkilemez.</p>
                  </div>
                </>
              )}

              {showTerms === 'privacy' && (
                <>
                  <h3>Gizlilik Politikası</h3>
                  <div className="login-modal__terms-body">
                    <p><strong>1. Toplanan Veriler</strong></p>
                    <p>Platformumuza kayıt olduğunuzda ad, soyad, e-posta adresi, telefon numarası ve CV bilgileriniz toplanır. Sosyal medya ile giriş yapıldığında ilgili platformdan profil bilgileriniz alınır.</p>

                    <p><strong>2. Verilerin Kullanım Amacı</strong></p>
                    <p>Kişisel verileriniz; hesap yönetimi, iş eşleştirme, kariyer analizi, iletişim ve hizmet iyileştirme amacıyla kullanılır.</p>

                    <p><strong>3. Verilerin Paylaşımı</strong></p>
                    <p>Kişisel verileriniz, iş başvurusu yaptığınız işverenler dışında üçüncü taraflarla paylaşılmaz. Yasal zorunluluk halinde yetkili mercilerle paylaşılabilir.</p>

                    <p><strong>4. Çerezler</strong></p>
                    <p>Platform, kullanıcı deneyimini iyileştirmek için çerezler kullanır. Tarayıcı ayarlarınızdan çerezleri yönetebilirsiniz.</p>

                    <p><strong>5. Veri Güvenliği</strong></p>
                    <p>Verileriniz SSL şifreleme ve endüstri standardı güvenlik önlemleri ile korunmaktadır.</p>

                    <p><strong>6. Haklarınız (KVKK)</strong></p>
                    <p>6698 sayılı Kişisel Verilerin Korunması Kanunu kapsamında; verilerinize erişim, düzeltme, silme ve işlemeye itiraz haklarına sahipsiniz. Taleplerinizi info@hirethink.ai adresine iletebilirsiniz.</p>

                    <p><strong>7. İletişim</strong></p>
                    <p>HireThink Yapay Zeka Teknolojileri A.Ş.<br />Bilişim Vadisi, Ayazağa, Vadi Istanbul Park Etabı, Kemerburgaz Caddesi D:7A Blok, 34475 Sarıyer / İstanbul<br />info@hirethink.ai | 0850 307 80 61<br />Vergi No: 4621090464</p>
                  </div>
                </>
              )}

              <button className="login-modal__terms-accept" onClick={() => setShowTerms(false)}>
                Anladım
              </button>
            </div>
          </div>
        )}

        {step === 'codeSent' && (
          <div className="login-modal__code-step">
            <div className="login-modal__code-header">
              <p><strong>{email}</strong> adresine gönderilen<br />6 haneli doğrulama kodunu girin</p>
            </div>

            {error && <p className="login-modal__error">{error}</p>}

            <div className="login-modal__code-inputs" onPaste={handleCodePaste}>
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (codeInputsRef.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="login-modal__code-digit"
                  value={digit}
                  onChange={(e) => handleCodeChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  autoFocus={i === 0}
                />
              ))}
            </div>

            <p className="login-modal__code-timer">
              Kod 5 dakika geçerlidir {countdown > 0 && <span>({formatCountdown(countdown)})</span>}
            </p>

            <div className="login-modal__code-actions">
              <button
                className="login-modal__resend"
                onClick={handleResendCode}
                disabled={loading || countdown > 240}
              >
                Kod gelmedi mi? Tekrar gönder
              </button>
            </div>

            <button
              className="login-modal__submit"
              onClick={() => handleVerifyCode(code.join(''))}
              disabled={loading || code.some(d => d === '')}
            >
              {loading ? <><Loader2 size={16} className="login-modal__spinner" /> Doğrulanıyor...</> : 'Doğrula'}
            </button>

            <button className="login-modal__back" onClick={() => { setStep('initial'); setError(''); }}>
              Geri dön
            </button>
          </div>
        )}

      </div>
    </div>,
    document.body
  );
};

export default LoginModal;
